/**
 * 金流數據（v2.29.0）
 *
 * 嵌在「營收數據 › 金流數據」Tab 裡（Revenue.js 的 activeProvider === 'OtherFee' 分支）。
 * **不是獨立頁** —— 外層的 Card / Row / padding 由 Revenue.js 提供。
 *
 * 三個檢視（取代共用的 Table / Chart —— 這一頁不再有 chart 按鈕）：
 *   表格  逐週一列，密度與欄位對齊其他三個 Tab；**會計填寫就在表格裡**
 *   總覽  KPI + 長條圖 + 依俱樂部，所有互動操作都在這
 *   歷史  舊的 OA / R2H 唯讀（資料源 cm_detail.json 停在 2026-01-11）
 *
 * ⚠️ 兩個口徑不要混：
 *   等值台幣  台幣幣商實際台幣 + USDT 以成交當時匯率換算 → 主視圖
 *   實際金流  只算真的有台幣在動的                      → 佣金按這個算
 *
 * ⚠️ 筆數缺資料時顯示「—」不是 0。台幣幣商 2025-12-17~12-31 只有月報表沒有逐筆。
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import CashflowEditCell from 'components/tiny/CashflowEditCell';
import TinyTooltip from 'components/tiny/TinyTooltip';
import TinyLoading, { TinyBusy, TinyLoadingOverlay } from 'components/tiny/TinyLoading';
import axios from 'axios';
import {
  Button, Modal, ModalBody, ModalFooter, ModalHeader, Nav, NavItem, NavLink, Table,
} from 'reactstrap';
import {
  cancelBtnStyle, inputStyle, labelStyle, modalBodyStyle, modalFooterStyle, modalHeaderStyle,
  saveBtnStyle, softBadge,
} from 'constant/adminTheme';
import classNames from 'classnames';

const C = {
  border: 'rgba(255,255,255,0.1)',
  inputBg: '#1a1f2b',
  text: '#fff',
  sub: 'rgba(255,255,255,0.8)',
  dim: 'rgba(255,255,255,0.4)',
  success: '#2dce89',
  danger: '#f5365c',
  warning: '#fb6340',
  // 沿用幣商後台的語意色：入金藍 / 出金橘
  inflow: '#4a90e2',
  outflow: '#e8763a',
};

/**
 * 每一組 switch 各自的色相 —— 全部取自專案既有的語意色盤（black-dashboard 那套），
 * 同飽和度、只換色相，所以區分得開又不打架。
 *
 * 指派原則：**層級越高、越接近主 accent**。
 *   檢視 = 靛藍（主 accent）→ 幣別 = 紫 → 幣商 = 金（對應幣商圓點）
 *   粒度 = 天藍 · 顯示 = 青 · 度量 = 綠 · 單位 = 橘
 *
 * ⚠️ 幣商那組用金色是刻意的 —— 跟表格裡第一家幣商的圓點同一個色系，
 * 視線從 switch 移到表格時對得起來。
 */
const ACCENT = {
  view: '102,126,234',      // 表格 / 總覽 / 歷史金流（主層級）
  currency: '153,102,255',  // 全部 / USDT / 台幣
  vendor: '255,194,86',     // 全部 / 各台幣幣商（清單來自 health.sources）
  granularity: '74,144,226',// 月 / 週 / 日
  show: '17,205,239',       // 買幣 / 賣幣 / 一起顯示
  metric: '45,206,137',     // 筆數 / 金額
  unit: '251,99,64',        // 台幣面額 / USDT
  sync: '45,206,137',       // 立即同步
};

// 表頭沿用其他三個 Tab 的慣例：rgba(...,0.2) 有色底 + 置中
const TH = (bg) => ({ backgroundColor: bg, textAlign: 'center', fontSize: '15px', whiteSpace: 'nowrap' });
const TD = { textAlign: 'center', fontSize: '14px', whiteSpace: 'nowrap' };

const fmt = (n) => (n === null || n === undefined || Number.isNaN(Number(n))
  ? '—' : Math.round(Number(n)).toLocaleString('en-US'));

const monthOf = (key) => (/^\d{8}-\d{8}$/.test(key)
  ? `${key.slice(0, 4)}-${key.slice(4, 6)}`
  : (key.length === 7 ? key : key.slice(0, 7)));

const label = (key) => (/^\d{8}-\d{8}$/.test(key)
  ? `${key.slice(4, 6)}/${key.slice(6, 8)}–${key.slice(13, 15)}/${key.slice(15, 17)}`
  : key);

const iso = (k, at) => `${k.slice(at, at + 4)}-${k.slice(at + 4, at + 6)}-${k.slice(at + 6, at + 8)}`;

/**
 * 這一週該不該顯示佣金？回傳它結算的月份，或 null。
 *
 * 佣金是月結，會計的做法是**填在涵蓋該月最後一天的那一週**：
 *   8月佣金 → 08/31–09/06（含 8/31）
 *   7月佣金 → 07/27–08/02（含 7/31）
 *   6月佣金 → 06/29–07/05（含 6/30）
 * 所以規則就是「這週有沒有涵蓋某個月的最後一天」，不是「這是不是該月第一列」。
 *
 * ⚠️ 而且該月必須**已經結束** —— 9 月還沒過完就不該有 9 月佣金，
 * 系統算出來的也只是半個月的數字，秀出來只會誤導。
 */
/**
 * 這個期間結束了沒？沒結束的那一期在表格裡**置頂、虛線框、標「進行中」**，
 * 會計三欄鎖住不給填（v2.44.0，Brad 2026-09-25）。
 * 原本整列藏起來、只在表格上方寫一行「進行中未列入（入金 … · 出金 …）」——
 * 但最常想看的就是最新這週，那一行太不起眼，也沒辦法跟上週逐欄對照。
 */
/**
 * 一根長條 → 下一層的粒度與期間（drill-down）。
 *
 * 月 → 週（該月整月）、週 → 日（該週七天）、日 → 無（已經是最細）。
 *
 * ⚠️ 回傳的 `from`/`to` 是**日期**不是 bucket key —— `/cashflow/series` 吃的是
 *    `from`/`to` + `granularity`，切幾桶由後端的 `_bucket()` 決定。
 *    這裡不要自己算週界，不然就會變成「前端一份、後端一份週期規則」，
 *    而這個 repo 已經因為那樣漂過好幾次。
 * 🔑 月 → 週時 `to` 用該月最後一天：後端會把**涵蓋到**這個區間的週都切出來，
 *    所以跨月的頭尾週也會出現，那是對的 —— 那幾天確實屬於這個月。
 */
const drillInto = (key, granularity) => {
  if (granularity === 'month' && /^\d{4}-\d{2}$/.test(key)) {
    const [y, m] = key.split('-').map(Number);
    const last = new Date(y, m, 0).getDate();
    return { granularity: 'week', from: `${key}-01`, to: `${key}-${String(last).padStart(2, '0')}` };
  }
  if (granularity === 'week' && /^\d{8}-\d{8}$/.test(key)) {
    return { granularity: 'day', from: iso(key, 0), to: iso(key, 9) };
  }
  return null;   // 粒度=日，沒有更細的了
};

const GRAN_LABEL = { month: '月', week: '週', day: '日' };
// 由粗到細。索引就是縮放層級，drill 與「切回上一層」都靠它算。
const GRAN_ORDER = ['month', 'week', 'day'];

const isClosed = (key) => {
  const today = new Date(new Date().toDateString());
  if (/^\d{8}-\d{8}$/.test(key)) return new Date(`${iso(key, 9)}T00:00:00`) < today;
  if (/^\d{4}-\d{2}$/.test(key)) {
    const [y, m] = key.split('-').map(Number);
    return new Date(y, m, 0) < today;                 // 該月最後一天
  }
  return new Date(`${key}T00:00:00`) < today;         // 粒度=日
};

// 進行中的期間走到第幾天，例「5/7」。週期長度照 key 算（開幕週是 9 天），粒度=日回 null
const progressOf = (key) => {
  const today = new Date(new Date().toDateString());
  const days = (a, b) => Math.round((b - a) / 86400000) + 1;
  if (/^\d{8}-\d{8}$/.test(key)) {
    const s = new Date(`${iso(key, 0)}T00:00:00`);
    const total = days(s, new Date(`${iso(key, 9)}T00:00:00`));
    return `${Math.min(days(s, today), total)}/${total}`;
  }
  if (/^\d{4}-\d{2}$/.test(key)) {
    const [y, m] = key.split('-').map(Number);
    return `${today.getDate()}/${new Date(y, m, 0).getDate()}`;
  }
  return null;
};

const closingMonthOf = (key) => {
  if (!/^\d{8}-\d{8}$/.test(key)) {
    return key.length === 7 ? key : null;      // 粒度=月時，key 本身就是月份
  }
  const start = new Date(`${iso(key, 0)}T00:00:00`);
  const end = new Date(`${iso(key, 9)}T00:00:00`);
  const eom = new Date(start.getFullYear(), start.getMonth() + 1, 0);   // 該月最後一天
  if (eom < start || eom > end) return null;                            // 沒涵蓋到月底
  if (eom >= new Date(new Date().toDateString())) return null;          // 該月還沒過完
  return `${eom.getFullYear()}-${String(eom.getMonth() + 1).padStart(2, '0')}`;
};

const Seg = ({ options, value, onChange, accent = '116,132,255' }) => (
  <Nav pills className="rev-seg-nav" style={{ '--tab-accent': accent }}>
    {options.map((o) => (
      <NavItem key={o.key}>
        <NavLink href="#" className={classNames('rev-seg-link', { active: value === o.key })}
          onClick={(e) => { e.preventDefault(); onChange(o.key); }}>
          {o.icon && <i className={`fa ${o.icon} mr-1`} />}{o.label}
        </NavLink>
      </NavItem>
    ))}
  </Nav>
);

/**
 * 同一個儲存格內把多個幣商上下疊起來。
 *
 * ⚠️ 刻意**不用「一個幣商三欄」**的做法 —— 那樣來一個幣商 C 就多三欄，版面會爆。
 * 欄位固定三欄、列高隨幣商數長高，加幾家都不會壞。
 */
const VENDOR_DOT = {
  twd_vendor_a: '#ffc256',
  twd_vendor_b: '#11cdef',
};
// 只有一家幣商時仍然畫成上下兩格，第二格是預留位。這樣下一家上線那天版面不會
// 整張跳動，平常也一眼看得出「這三欄是按幣商分的」而不是普通儲存格。
// ⚠️ 佔位列**刻意不寫幣商名稱**：下一家叫什麼、什麼時候來都還沒定，
//    寫死名字就是把猜測畫在畫面上。真的有第二家時它會被真資料取代（2026-09 長安幣商）。
const RESERVED_SLOT = { source_id: '__reserved__', display_name: '未設定', reserved: true };

function VendorStack({ vendors, render, showLabel = true, align = 'flex-end' }) {
  if (!vendors.length) return null;
  const list = vendors.length === 1 ? [...vendors, RESERVED_SLOT] : vendors;
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {list.map((v, i) => (
        <div key={v.source_id}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: align, gap: 6,
            // 高度要跟其他欄的 stack 一致，行才對得齊
            minHeight: 24,
            padding: '3px 0',
            borderTop: i === 0 ? 'none' : '1px dashed rgba(255,255,255,0.12)',
            opacity: v.reserved ? 0.35 : 1,
          }}>
          {showLabel && (
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)',
              display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 'auto' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%',
                background: v.reserved ? 'transparent' : (VENDOR_DOT[v.source_id] || '#f5365c'),
                border: v.reserved ? '1px solid rgba(255,255,255,0.4)' : 'none',
                display: 'inline-block' }} />
              {v.display_name}
            </span>
          )}
          <span>{v.reserved ? '—' : render(v)}</span>
        </div>
      ))}
    </div>
  );
}

const signed = (n) => `${n > 0 ? '+' : ''}${fmt(n)}`;

/**
 * 「差 +106」「佣金差 211」那個標記（v2.52.0 差異確認）。
 *
 *   mismatch      紅字；有編輯權限的人點下去「確認差異」
 *   acknowledged  灰字「· 已確認」，滑過看原因；有權限的人點下去可以改說明或撤銷
 *
 * 確認過的是**灰色不是綠色** —— 它本來就不相符，只是有人查過、說明了原因。
 * 確認綁住當時的兩個數字，任何一邊變了後端就回 mismatch + ack_stale（滑過講得出來）。
 */
function DiffMark({ chk, text, canWrite, onOpen, style }) {
  const acked = chk.status === 'acknowledged';
  const stale = !acked ? chk.ack_stale : null;
  let tip = null;
  if (acked) {
    tip = (
      <div style={{ lineHeight: 1.6 }}>
        <div style={{ fontWeight: 600 }}>已確認：{chk.ack.category_label}</div>
        <div>{chk.ack.reason}</div>
        <div style={{ color: C.dim, marginTop: 4 }}>{chk.ack.acked_by}・{chk.ack.acked_at}</div>
      </div>
    );
  } else if (stale) {
    tip = `之前確認過差 ${signed(stale.diff)}（${stale.reason}），之後數字變了，要重新確認`;
  } else if (canWrite) {
    tip = '查清楚原因後點一下，確認這個差額';
  }
  const color = acked ? C.dim : C.danger;
  const body = (
    <span
      role={canWrite ? 'button' : undefined}
      onClick={canWrite ? onOpen : undefined}
      style={{
        color, fontWeight: 600, whiteSpace: 'nowrap',
        cursor: canWrite ? 'pointer' : 'default',
        borderBottom: canWrite ? `1px dashed ${color}` : 'none',
        ...style,
      }}>
      {text}{acked && <span style={{ fontWeight: 500 }}> · 已確認</span>}
    </span>
  );
  return tip ? <TinyTooltip maxWidth={320} content={tip}>{body}</TinyTooltip> : body;
}

const pct = (r) => `${+(r * 100).toFixed(2)}%`;
const fmt2 = (n) => Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const wan = (n) => (n % 10000 === 0 ? `${(n / 10000).toLocaleString('en-US')} 萬` : fmt(n));

// 有級距才回一句話（「700 萬以下 4%、超過 3.5%」），平率回 null
const tierText = (tiers) => (!tiers || tiers.length < 2 ? null : tiers.map((t, i) => (
  t.max_amount == null ? `超過 ${wan(tiers[i - 1].max_amount)} ${pct(t.rate)}`
    : `${wan(t.max_amount)}以下 ${pct(t.rate)}`)).join('、'));

/**
 * 「幣商佣金 · 系統計算」那格的算式（v2.48.0，Brad 選的 B 案）。
 *
 * 佣金是月結（兩家都是），能拿來對帳的只有月佣金，所以算式掛在月佣金那一格，
 * 🚫 不做「每週應計佣金」欄 —— 週數字沒有帳單可以對、跨月的週要拆、有級距時
 *    月底前根本算不出來。
 * 級距看的是**整月總量**不是逐筆，有級距的月份（蘋果 2026-07）要把門檻講出來，
 * 不然看到 3.5% 會以為算錯。
 */
function CommissionFormula({ vc, month, name }) {
  const [y, m] = month.split('-').map(Number);
  const last = new Date(y, m, 0).getDate();
  const tiers = [['入金', tierText(vc.buy_tiers)], ['出金', tierText(vc.sell_tiers)]]
    .filter(([, t]) => t);
  const line = (lb, color, vol, rate, fee) => (
    <tr>
      <td style={{ color, paddingRight: 10 }}>{lb}</td>
      <td style={{ textAlign: 'right' }}>{fmt(vol)}</td>
      <td style={{ padding: '0 8px' }}>× {pct(rate)}</td>
      <td style={{ textAlign: 'right' }}>{fmt2(fee)}</td>
    </tr>
  );
  return (
    <>
      <div style={{ fontWeight: 600, color: C.text, marginBottom: 6 }}>
        {name} {y} 年 {m} 月應付佣金
      </div>
      <table style={{ borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums' }}>
        <tbody>
          {line('入金', C.inflow, vc.buy_volume, vc.buy_rate, vc.buy_fee)}
          {line('出金', C.outflow, vc.sell_volume, vc.sell_rate, vc.sell_fee)}
          <tr style={{ borderTop: `1px solid ${C.border}` }}>
            <td colSpan={3} style={{ paddingTop: 4 }}>合計</td>
            <td style={{ textAlign: 'right', paddingTop: 4, fontWeight: 600, color: C.text }}>
              {fmt2(vc.expected)}</td>
          </tr>
        </tbody>
      </table>
      <div style={{ marginTop: 6, color: C.dim }}>
        入金、出金 ＝ {m}/1–{m}/{last} 每天的交收金額加總
        {tiers.length > 0 && (
          <><br />級距看整月總量：{tiers.map(([lb, t]) => `${lb} ${t}`).join('；')}</>
        )}
      </div>
    </>
  );
}

export default function TinyCashflow({ view = 'table', onViewChange, renderLegacy }) {
  const [granularity, setGranularity] = useState('week');
  const [currency, setCurrency] = useState('all');
  const [vendor, setVendor] = useState('all');
  const [metric, setMetric] = useState('amount');
  const [unit, setUnit] = useState('twd');        // 金額時：台幣面額 / USDT
  const [show, setShow] = useState('both');
  const [club, setClub] = useState('all');   // 俱樂部篩選（總覽用）

  const [range, setRange] = useState(null);
  /**
   * 每個粒度各自「上次看的期間」。
   *
   * 🔑 粒度在這一頁的語意是**縮放層級**（月 → 週 → 日），所以
   *    「breadcrumb 的返回上一層」與「直接按上面的粒度」本來就該是同一件事 ——
   *    兩條路都走 `goToGranularity()`，不要各寫一套。
   *
   * 為什麼需要記：鑽進去之後期間會被縮窄。如果手動把粒度切回「週」卻沿用
   * 那個縮窄的期間，畫面上就只剩一根柱子 —— 但使用者鑽進來之前明明在看整段歷史
   *（Brad 2026-09-10 回報）。
   *
   * ⚠️ 手動改日期時要**清掉比目前更細的層級**：那些期間是從舊範圍推出來的，
   *    範圍換了它們就失效，留著會讓「往下鑽再切回來」跳到不相干的月份。
   */
  const [rangeByGran, setRangeByGran] = useState({});
  const [series, setSeries] = useState(null);
  const [costs, setCosts] = useState({});
  const [manual, setManual] = useState({});
  // 佣金是月結，doc 上的 attributed_month 決定它屬於哪個月 —— 光有金額不夠，
  // 對帳（系統應付 vs 會計實付）要靠它去 costs 找對應月份。
  const [manualMonth, setManualMonth] = useState({});
  const [canWrite, setCanWrite] = useState(false);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  // v2.40.0：首屏原本什麼都不顯示（loading 起始是 false、health 那支又沒有旗標），
  // 空的圖表會停在畫面上好幾秒。改成「還沒有 series 就顯示 loading，失敗就顯示失敗」，
  // 兩者缺一個都會變成永遠轉圈或永遠空白。
  const [loadError, setLoadError] = useState(null);
  // 告警列預設收合成一行（v2.46.0）：夠醒目但不佔畫面，點開才展開清單
  const [alertsOpen, setAlertsOpen] = useState(false);
  // 「彙總不符」點開的明細 { key, loading, data, error }（v2.46.0）
  const [mismatchView, setMismatchView] = useState(null);
  // 幣商實際交收：系統算 vs 會計填，逐週 × 逐幣商（後端算，跟告警同一支，v2.52.0）
  const [checks, setChecks] = useState({});
  // 「確認差異」視窗（v2.52.0）
  const [ackView, setAckView] = useState(null);

  // 預設看**全部** —— 資料從 2025-12-13 起，預設只給 90 天會看不出趨勢
  useEffect(() => {
    axios.get('/api/tiny/cashflow/health').then((r) => {
      if (r.data?.code !== 1) { setLoadError(r.data?.msg || '讀取金流設定失敗'); return; }
      setHealth(r.data.data);
      setRange({ from: r.data.data.settings?.start_date || '2025-12-13',
        to: new Date().toISOString().slice(0, 10) });
    }).catch((e) => setLoadError(e.response?.data?.msg || e.message));
  }, []);

  // 來源狀態鈕跟著最新一次同步走 —— 按完「立即同步」要重抓，不然按完還是舊狀態。
  // 只換 health、不動期間（期間只在首次載入時由 start_date 決定）。
  const refreshHealth = useCallback(() => axios.get('/api/tiny/cashflow/health')
    .then((r) => { if (r.data?.code === 1) setHealth(r.data.data); })
    .catch(() => {}), []);

  /**
   * 「全部期間」＝ 資料起始日 ~ 今天。
   *
   * ⚠️ 起始日跟畫面初始值用**同一個來源**（health.settings.start_date），
   *    不要另外寫死一個 —— 兩邊漂掉的話，「全部期間」按下去會跟剛進頁面時
   *    看到的範圍不一樣。
   */
  const fullRange = useMemo(() => ({
    from: health?.settings?.start_date || '2025-12-13',
    to: new Date().toISOString().slice(0, 10),
  }), [health]);

  const isFullRange = !!range && range.from === fullRange.from && range.to === fullRange.to;

  // 台幣幣商清單從 health.sources 長出來，**不寫死**。幣商會進出（蘋果 → 蘋果＋長安 → …），
  // 寫死等於每來一家改一次前端；而且切換器的 label 就是 DB 的 display_name，
  // 改名（幣商A → 蘋果幣商）也不用動 code。
  const twdVendors = useMemo(
    () => (health?.sources || []).filter((x) => x.currency === 'TWD'),
    [health]);

  const sourceIds = useMemo(() => {
    if (currency === 'usdt') return 'usdt_self';
    if (currency === 'twd') {
      return vendor === 'all' ? twdVendors.map((x) => x.source_id).join(',') : vendor;
    }
    return '';
  }, [currency, vendor, twdVendors]);

  const load = useCallback(async () => {
    if (!range?.from || !range?.to) return;
    setLoading(true);
    setLoadError(null);
    try {
      const qs = new URLSearchParams({ from: range.from, to: range.to, granularity });
      if (sourceIds) qs.set('sources', sourceIds);
      // 俱樂部篩選只影響總覽的 KPI 與圖表；表格是結算檢視、一律看全部
      if (view === 'summary' && club !== 'all') qs.set('clubs', club);
      const s = await axios.get(`/api/tiny/cashflow/series?${qs}`);
      if (s.data?.code !== 1) { setLoadError(s.data?.msg || '讀取金流數據失敗'); return; }
      const data = s.data.data;
      setSeries(data);

      // 週層級的人工項（幣商實際交收 / 佣金）
      // ⚠️ 一個 request 拿完整段期間 —— 原本一週一個，週期一多就是幾十次往返
      //（`gunicorn -w 2` 一次只吃 2 個）。雜項費用頁同樣的毛病更嚴重。
      const weeks = (data.buckets || [])
        .map((b) => b.key).filter((k) => /^\d{8}-\d{8}$/.test(k)).sort();
      const map = {};
      const monthMap = {};
      let writable = false;
      if (weeks.length) {
        const mres = await axios.get('/api/tiny/cashflow/manual/bulk'
          + `?from=${weeks[0]}&to=${weeks[weeks.length - 1]}`);
        const d = mres.data?.data || {};
        writable = !!d.can_write;
        Object.entries(d.weeks || {}).forEach(([wk, wd]) => {
          (wd.common || []).forEach((it) => { map[`${wk}|${it.item_key}`] = it.amount; });
          Object.entries(wd.vendor_items || {}).forEach(([k, it]) => {
            map[`${wk}|${k}`] = it.amount;
            if (it.attributed_month) monthMap[`${wk}|${k}`] = it.attributed_month;
          });
        });
      }
      setManual(map);
      setManualMonth(monthMap);

      // 成本區（系統算的應付）。⚠️ 要抓的月份 = 畫面上的月份 **∪ 佣金實際歸屬的月份**
      // —— 佣金可以填在任何一週、歸屬到任何一個已結束的月份，所以歸屬月份可能
      // 落在畫面範圍外；只抓畫面上的月份會讓那筆佣金的「應付」變成空的。
      // 順序也因此不能顛倒：要先讀完 manual 才知道有哪些歸屬月份。
      const months = [...new Set([
        ...(data.buckets || []).map((b) => monthOf(b.key)),
        ...Object.values(monthMap),
      ])].filter(Boolean).sort();
      const [cr, ck] = await Promise.all([
        months.length
          ? axios.get(`/api/tiny/cashflow/cost?months=${encodeURIComponent(months.join(','))}`)
          : null,
        weeks.length
          ? axios.get('/api/tiny/cashflow/settlement-checks'
            + `?from=${weeks[0]}&to=${weeks[weeks.length - 1]}`)
          : null,
      ]);
      setCosts(cr?.data?.data || {});
      setChecks(ck?.data?.data || {});
      setCanWrite(writable);

    } catch (e) {
      setLoadError(e.response?.data?.msg || e.message);
    } finally { setLoading(false); }
  }, [range, granularity, sourceIds, club, view]);

  useEffect(() => { load(); }, [load]);

  const saveManual = async (week, itemKey, itemLabel, amount, attributedMonth) => {
    await axios.post('/api/tiny/cashflow/manual', {
      week, item_key: itemKey, item_label: itemLabel, amount, attributed_month: attributedMonth,
    });
    await load();
  };

  const doSync = async () => {
    setSyncing(true);
    try { await axios.post('/api/tiny/cashflow/sync', { mode: 'hot' }); await load(); }
    finally { await refreshHealth(); setSyncing(false); }
  };

  const buckets = series?.buckets || [];

  /* 🔴 這四個 helper 必須定義在**控制列 JSX 之前**。
        粒度那組 `<Seg onChange={goToGranularity}>` 在檔案上方（約 490 行），
        而它們原本寫在 chartData 附近（約 780 行）—— `const` 有 TDZ，
        render 走到控制列時還沒初始化，直接丟
        `Cannot access 'goToGranularity' before initialization`，整頁白掉。
        ⚠️ 編譯是綠的、lint 也沒話說，只有真的開頁面才會發現。 */
  /** 由粗到細，`GRAN_ORDER.indexOf()` 就是縮放層級 */
  const coarserGran = GRAN_ORDER[GRAN_ORDER.indexOf(granularity) - 1] || null;

  /** 點某一根長條 → 鑽進它的下一層粒度 */
  const doDrill = (idx) => {
    const b = buckets[idx];
    if (!b) return;
    const next = drillInto(b.key, granularity);
    if (!next) return;                       // 粒度=日，沒有更細的
    const to = { from: next.from, to: next.to };
    setRangeByGran((m) => ({ ...m, [granularity]: range, [next.granularity]: to }));
    setRange(to);
    setGranularity(next.granularity);
  };

  /**
   * 切到某個粒度 —— **breadcrumb 與上面那組粒度按鈕共用這一支**。
   *
   * 有記到那個粒度的期間就還原；沒有（第一次去到那一層）就沿用目前的期間。
   * 離開前先把目前這一層的期間記下來，等一下切回來才回得到原地。
   */
  const goToGranularity = (g) => {
    if (g === granularity) return;
    setRangeByGran((m) => ({ ...m, [granularity]: range }));
    const remembered = rangeByGran[g];
    if (remembered) {
      setRange(remembered);
    } else if (GRAN_ORDER.indexOf(g) < GRAN_ORDER.indexOf(granularity)) {
      /* 🔴 往**更粗**切、又沒去過那一層 → 退回全部期間，不要沿用目前的期間。
            先前是沿用，於是「預設週 → 直接點長條鑽到日 → 按月」會拿那七天去
            切月，畫面只剩一根柱子（Brad 2026-09-11 玩到）。
            拿七天去看「月」本來就沒有意義；沒有記憶可還原時，全部期間才是
            合理的預設。往更細切則維持沿用 —— 那是在現有範圍裡看得更仔細。 */
      setRange(fullRange);
    }
    setGranularity(g);
  };

  /**
   * 手動挑日期。
   *
   * ⚠️ 除了改期間，還要**清掉比目前更細的層級**所記的期間 —— 那些是從舊範圍
   *    推出來的（例如在 5 月鑽出來的「日」範圍），範圍換成 7 月之後它們就失效，
   *    留著會讓「往下鑽再切回來」跳到不相干的月份。
   */
  const onPickDate = (which, value) => {
    setRange((r) => {
      const next = { ...r, [which]: value };
      const i = GRAN_ORDER.indexOf(granularity);
      setRangeByGran((m) => {
        const kept = {};
        GRAN_ORDER.slice(0, i).forEach((g) => { if (m[g]) kept[g] = m[g]; });
        return { ...kept, [granularity]: next };
      });
      return next;
    });
  };

  const canDrill = granularity !== 'day';

  const t = series?.total || {};
  // 俱樂部清單：只有四家的玩家在用我們的幣商，所以下拉很短。
  // 用 health 帶回來的清單（不受當前篩選影響），不然選了某一家之後
  // 下拉就只剩那一家、選不回去了。
  const clubOptions = health?.clubs || [];
  // 每個來源各自一顆狀態鈕（v2.44.0）。原本只有一行「最後同步 … ⚠ 有來源失敗」——
  // 看不出是哪一家壞，而且「最後同步」取的是所有來源裡最新的那次：
  // 2026-09-24 長安分頁改名、同步停了大半天，畫面上照樣寫著剛同步過。
  // 還沒接上的（設定建了、對方還沒給 Sheet）不顯示 —— 它不是壞掉，
  // 一直亮著的紅字等於沒有告警，真的出事時沒人會相信。
  const syncSources = (health?.sources || []).filter((s) => s.configured);
  const syncStateOf = (s) => {
    const st = s.last_sync?.status;
    if (!st) return 'none';
    if (st === 'failed') return 'failed';
    // 同步中不是失敗 —— 蘋果一次要 8~20 秒，那段時間開頁不該看到紅字
    if (st === 'running') return 'running';
    // 同步成功，但「以對方最新為準」的刪除被擋下來（一次要刪太多，像是讀壞了）
    if (s.last_sync?.prune_skipped) return 'warn';
    return 'ok';
  };

  /**
   * 幣商專屬欄位：佣金（系統）· 佣金（會計填）· 實際交收（會計填）
   *
   * 台幣/USDT 的拆解欄拿掉了 —— 上面的幣別 switch 就能分，表格再放一次是重複。
   * 這三欄改成**每個幣商各一組**，用上下兩層表頭 + 各自的顏色區隔：
   *
   *     選「全部」        → 列出所有台幣幣商（USDT 沒有佣金，不佔欄）
   *     選「USDT」        → 完全不出現
   *     選「台幣 + 全部」  → 列出所有台幣幣商
   *     選「台幣 + 某一家」 → 只出現那一家那一組
   */
  const vendorCols = (() => {
    if (currency === 'usdt') return [];
    const twd = (health?.sources || []).filter((x) => x.currency === 'TWD');
    if (currency === 'twd' && vendor !== 'all') return twd.filter((x) => x.source_id === vendor);
    return twd;
  })();

  // 某個幣商每個月已登記的佣金金額。costs 是權威（後端按 attributed_month 查），
  // 但它只涵蓋抓回來的月份，所以再併上畫面上看到的 attributed_month。
  const commissionByMonth = (sourceId) => {
    const out = {};
    Object.entries(costs).forEach(([m, panel]) => {
      const vc = (panel?.vendors || []).find((x) => x.source_id === sourceId);
      if (vc && vc.actual != null) out[m] = vc.actual;
    });
    Object.entries(manualMonth).forEach(([k, m]) => {
      if (k.endsWith(`|vendor_commission__${sourceId}`) && out[m] == null) {
        out[m] = manual[k];
      }
    });
    return out;
  };

  // 該月是否已經結束（月結佣金要月底過完才算得出來）
  const monthEnded = (m) => {
    if (!/^\d{4}-\d{2}$/.test(m)) return true;
    const [y, mo] = m.split('-').map(Number);
    return new Date(y, mo, 0) < new Date(new Date().toDateString());
  };

  // 預設帶「最近一個已結束、還沒登記過的月份」—— 常見情況一次就對，不用改
  /**
   * 佣金月份的預設值 ＝ **下一個該填的月份**：從這家幣商最早有資料的月份往前走，
   * 第一個還沒登記的就是它，最遠走到本月。
   *
   * ⚠️ 以前是「從上個月往回找第一個沒登記的」—— 蘋果九個月全登記過，它就一路
   *    退到 2025-11（幣商還沒合作的月份）。Brad 2026-09-21：8 月已經登記、已經結束，
   *    下一次要填的一定是 9 月；長安 9 月才開始，也是 9 月。
   * 下界用 health 的 earliest_data_date（每家不同），沒有就退回本月。
   * 本月還沒結束會由 commissionNotice 提示，不阻擋（廠商可能提前開帳單）。
   */
  const defaultCommissionMonth = (sourceId) => {
    const filled = commissionByMonth(sourceId);
    const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const now = new Date();
    const thisMonth = ym(now);
    const src = (health?.sources || []).find((x) => x.source_id === sourceId);
    if (!src?.earliest_data_date) return thisMonth;
    const d = new Date(src.earliest_data_date.slice(0, 7) + '-01T00:00:00');
    for (let i = 0; i < 120; i += 1) {
      const m = ym(d);
      if (m >= thisMonth) return thisMonth;
      if (filled[m] == null) return m;
      d.setMonth(d.getMonth() + 1);
    }
    return thisMonth;
  };

  // ⚠️ 一律**只告警不阻擋**。覆蓋是合法操作（更正金額），月份沒結束也可能是
  // 廠商提前開帳單 —— 依 Brad 的原則不規定他什麼時候填，只是把後果講清楚。
  const commissionNotice = (sourceId, currentMonth) => (m) => {
    if (!m) return null;
    if (!/^\d{4}-\d{2}$/.test(m)) return '月份格式需為 YYYY-MM';
    const notes = [];
    const filled = commissionByMonth(sourceId);
    if (m !== currentMonth && filled[m] != null) {
      notes.push(`${m} 已登記 ${fmt(filled[m])}，儲存會覆蓋`);
    }
    if (!monthEnded(m)) notes.push(`${m} 還沒結束，系統算的應付還不準`);
    return notes.length ? notes.join('；') : null;
  };

  // 今天的只寫時分，其他日子加月日（withDate 強制加）
  const fmtSyncTime = (ts, withDate = false) => {
    if (!ts) return null;
    const d = ts instanceof Date ? ts : new Date(ts);
    if (Number.isNaN(d.getTime())) return null;
    const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    return (!withDate && d.toDateString() === new Date().toDateString())
      ? hm : `${d.getMonth() + 1}/${d.getDate()} ${hm}`;
  };

  // 進行中那一列的「截至」：各來源最後一次**成功**同步裡最新的那個。
  // ⚠️ 用 Date 比、不要字串排序 —— 後端回的是 'Fri, 25 Sep 2026 …' 那種格式，
  //    字串排序跨日就錯（舊的 lastSyncAt 就是這樣寫的）。
  const dataAsOf = (() => {
    const ts = syncSources.map((s) => new Date(s.last_ok_at || NaN))
      .filter((d) => !Number.isNaN(d.getTime()));
    return ts.length ? new Date(Math.max(...ts)) : null;
  })();

  const SYNC_TONE = {
    ok: { dot: C.success, border: C.border, bg: 'rgba(255,255,255,0.05)' },
    running: { dot: C.dim, border: C.border, bg: 'rgba(255,255,255,0.05)' },
    none: { dot: C.dim, border: C.border, bg: 'rgba(255,255,255,0.05)' },
    warn: { dot: C.warning, border: 'rgba(251,99,64,0.5)', bg: 'rgba(251,99,64,0.1)' },
    failed: { dot: C.danger, border: 'rgba(245,54,92,0.55)', bg: 'rgba(245,54,92,0.12)' },
  };

  // 一顆狀態鈕 ＝ 圓點（狀態）＋ 名稱 ＋ 時間；有 Sheet 的點下去開對方的 Sheet
  const renderSyncChip = (s) => {
    const state = syncStateOf(s);
    const tone = SYNC_TONE[state];
    const lastOk = fmtSyncTime(s.last_ok_at, true);
    const text = {
      ok: fmtSyncTime(s.last_ok_at),
      warn: fmtSyncTime(s.last_ok_at),
      running: '同步中…',
      none: '尚未同步',
      failed: `同步失敗 · 最後成功 ${lastOk || '—'}`,
    }[state];
    const tip = (
      <>
        {state === 'failed' && (
          <>
            <strong>同步失敗</strong>：{s.last_sync?.error}
            <br />
            資料停在 <strong>{lastOk || '—'}</strong>，之後的交易還沒進來。
          </>
        )}
        {state === 'warn' && s.last_sync?.prune_skipped}
        {state === 'running' && <>正在同步（上一次成功 {lastOk || '—'}）</>}
        {(state === 'ok' || state === 'none') && <>最後成功同步 {lastOk || '—'}</>}
        {s.sheet_url && <><br />點一下開 {s.display_name} 的 Google Sheet</>}
      </>
    );
    const style = {
      height: 30, boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '0 10px', borderRadius: 7, border: `1px solid ${tone.border}`, background: tone.bg,
      color: C.text, fontSize: 12, lineHeight: 1, whiteSpace: 'nowrap', textDecoration: 'none',
    };
    const inner = (
      <>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: tone.dot, flexShrink: 0 }} />
        <span style={{ fontWeight: 600 }}>{s.display_name}</span>
        <span style={{ color: state === 'failed' ? C.danger : C.dim }}>{text}</span>
        {s.sheet_url && (
          <i className="fa fa-arrow-up-right-from-square" style={{ fontSize: 10, color: C.dim }} />
        )}
      </>
    );
    return (
      <TinyTooltip key={s.source_id} maxWidth={380} placement="bottom" content={tip}>
        {s.sheet_url
          ? <a href={s.sheet_url} target="_blank" rel="noopener noreferrer"
              style={{ ...style, cursor: 'pointer' }}>{inner}</a>
          : <span style={style}>{inner}</span>}
      </TinyTooltip>
    );
  };

  // ---------- 「彙總不符」明細（v2.46.0）----------
  // 判讀規則在後端 aggregate_service.mismatch_days（跟告警共用同一份），這裡只負責畫。
  const rangeOfKey = (key) => {
    if (/^\d{8}-\d{8}$/.test(key)) return [iso(key, 0), iso(key, 9)];
    if (/^\d{4}-\d{2}$/.test(key)) {
      const [y, m] = key.split('-').map(Number);
      return [`${key}-01`, `${key}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`];
    }
    return [key, key];
  };
  const openMismatch = async (key) => {
    const [from, to] = rangeOfKey(key);
    setMismatchView({ key, loading: true, data: null, error: null });
    try {
      const qs = new URLSearchParams({ from, to });
      if (sourceIds) qs.set('sources', sourceIds);
      const r = await axios.get(`/api/tiny/cashflow/mismatch-days?${qs}`);
      if (r.data?.code !== 1) throw new Error(r.data?.msg || '讀取失敗');
      setMismatchView({ key, loading: false, data: r.data.data, error: null });
    } catch (e) {
      setMismatchView({ key, loading: false, data: null, error: e.response?.data?.msg || e.message });
    }
  };
  const VERDICT = {
    club_swap: { color: C.success, text: () => '總數一致，只是記在不同俱樂部（不影響交收）' },
    pending: { color: C.warning, text: (g) => `月報表多 ${fmt(g.diff)}：日明細還沒填付款人員，我們不算，請對方確認` },
    total_diff: { color: C.danger, text: (g) => `總數差 ${fmt(g.diff)}，要確認是哪一邊記錯` },
  };
  const WEEKDAY = '日一二三四五六';
  const mmTd = { padding: '4px 8px', textAlign: 'right', whiteSpace: 'nowrap' };
  const renderMismatchModal = () => {
    const mv = mismatchView;
    const close = () => setMismatchView(null);
    return (
      <Modal isOpen={!!mv} toggle={close} centered size="lg">
        <ModalHeader toggle={close} style={modalHeaderStyle}>
          <i className="fa fa-magnifying-glass mr-2" style={{ color: C.warning }} />
          {mv ? label(mv.key) : ''}　彙總不符
        </ModalHeader>
        <ModalBody style={{ ...modalBodyStyle, fontSize: 13 }}>
          {mv?.loading && <TinyLoading text="讀取中" variant="block" />}
          {mv?.error && <div style={{ color: C.danger }}>讀取失敗：{mv.error}</div>}
          {mv?.data && mv.data.length === 0 && (
            <div style={{ color: C.dim }}>這段期間已經沒有對不起來的地方了（對方可能已經改好）。</div>
          )}
          {mv?.data?.length > 0 && (
            <>
              <div style={{ color: C.dim, fontSize: 12, marginBottom: 10 }}>
                月報表是幣商自己的彙總，日明細是逐筆紀錄。
              </div>
              {mv.data.map((g) => {
                const v = VERDICT[g.verdict] || VERDICT.total_diff;
                const dd = new Date(`${g.date}T00:00:00`);
                return (
                  <div key={`${g.source_id}|${g.date}|${g.direction}`}
                    style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <b>{dd.getMonth() + 1}/{dd.getDate()}（{WEEKDAY[dd.getDay()]}）</b>
                      <span>{g.display_name}</span>
                      <span style={{ color: g.direction === 'buy' ? C.inflow : C.outflow }}>
                        {g.direction === 'buy' ? '入金' : '出金'}</span>
                      <span style={{ marginLeft: 'auto', color: v.color, fontWeight: 600, fontSize: 12 }}>{v.text(g)}</span>
                    </div>
                    <table style={{ width: '100%', marginTop: 6, color: C.sub }}>
                      <thead>
                        <tr style={{ color: C.dim, fontSize: 12 }}>
                          <th style={{ ...mmTd, textAlign: 'left' }}>俱樂部</th>
                          <th style={mmTd}>月報表</th><th style={mmTd}>日明細</th><th style={mmTd}>差額</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.rows.map((r) => {
                          const d = (r.monthly || 0) - (r.detail || 0);
                          return (
                            <tr key={r.club_id} style={{ borderTop: `1px solid ${C.border}` }}>
                              <td style={{ ...mmTd, textAlign: 'left' }}>{r.club_id}</td>
                              <td style={mmTd}>{fmt(r.monthly || 0)}</td>
                              <td style={mmTd}>{fmt(r.detail || 0)}</td>
                              <td style={{ ...mmTd, color: C.warning }}>{d > 0 ? '+' : ''}{fmt(d)}</td>
                            </tr>
                          );
                        })}
                        <tr style={{ borderTop: `1px solid ${C.border}`, fontWeight: 600, color: C.text }}>
                          <td style={{ ...mmTd, textAlign: 'left' }}>當天合計</td>
                          <td style={mmTd}>{fmt(g.monthly_total)}</td>
                          <td style={mmTd}>{fmt(g.detail_total)}</td>
                          <td style={{ ...mmTd, color: Math.abs(g.diff) <= 1 ? C.success : C.danger }}>
                            {g.diff > 0 ? '+' : ''}{fmt(g.diff)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </>
          )}
        </ModalBody>
      </Modal>
    );
  };

  // ---------- 確認差異（v2.52.0）----------
  // 規則（綁住當下的數字、原因必填、誰能確認）都在後端 diff_ack，這裡只負責畫與送出。
  const openAck = ({ kind, vendor, period, periodLabel, chk }) => {
    const acked = chk.status === 'acknowledged';
    setAckView({
      kind, period, periodLabel,
      sourceId: vendor.source_id, name: vendor.display_name,
      calculated: chk.calculated, actual: chk.actual, diff: chk.diff,
      ack: acked ? chk.ack : null,
      stale: acked ? null : (chk.ack_stale || null),
      category: (acked && chk.ack.category) || '',
      reason: (acked && chk.ack.reason) || '',
      busy: null, error: null,
    });
  };
  const closeAck = () => { if (!ackView?.busy) setAckView(null); };
  const ackRequest = async (url, body, busy) => {
    setAckView((v) => ({ ...v, busy, error: null }));
    try {
      const r = await axios.post(url, body);
      if (r.data?.code !== 1) throw new Error(r.data?.message || '儲存失敗');
      setAckView(null);
      // 告警列也要跟著變（交收那則告警會當場結案）
      await Promise.all([load(), refreshHealth()]);
    } catch (e) {
      setAckView((v) => v && { ...v, busy: null, error: e.response?.data?.message || e.message });
    }
  };
  const submitAck = () => ackRequest('/api/tiny/cashflow/diff-ack', {
    kind: ackView.kind, source_id: ackView.sourceId, period: ackView.period,
    category: ackView.category, reason: ackView.reason,
    // 畫面上看到的數字：開著頁面的期間對方改了表，後端會擋下來請使用者重新整理
    seen_calculated: ackView.calculated, seen_actual: ackView.actual,
  }, 'save');
  const revokeAck = () => ackRequest('/api/tiny/cashflow/diff-ack/revoke', {
    kind: ackView.kind, source_id: ackView.sourceId, period: ackView.period,
  }, 'revoke');

  const renderAckModal = () => {
    const av = ackView;
    const cats = health?.diff_ack_categories || [];
    const canSubmit = !!av && !!av.category && !!av.reason.trim() && !av.busy;
    const num = (k, v, color) => (
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: C.dim }}>{k}</div>
        <div style={{ fontSize: 18, fontWeight: 600, color: color || C.text }}>{v}</div>
      </div>
    );
    const box = (color) => ({
      ...softBadge(color, { fontSize: 12, fontWeight: 500, padding: '8px 12px', borderRadius: 6 }),
      lineHeight: 1.6, marginBottom: 14,
    });
    return (
      <Modal isOpen={!!av} toggle={closeAck} centered>
        <ModalHeader toggle={closeAck} style={modalHeaderStyle}>
          {av?.ack ? '已確認的差異' : '確認差異'}
        </ModalHeader>
        {av && (<>
          <ModalBody style={{ ...modalBodyStyle, fontSize: 13 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', display: 'inline-block',
                background: VENDOR_DOT[av.sourceId] || C.danger }} />
              <b>{av.name}</b>
              <span style={{ color: C.sub }}>{av.periodLabel}</span>
            </div>
            <div style={{ display: 'flex', gap: 12, padding: '10px 14px', marginBottom: 14,
              border: `1px solid ${C.border}`, borderRadius: 8 }}>
              {num('會計填', fmt(av.actual))}
              {num('系統算', fmt(av.calculated))}
              {num('差', signed(av.diff), av.ack ? C.sub : C.danger)}
            </div>
            {/* 中性色，不是綠色：跟表格一樣，已確認 ≠ 相符 */}
            {av.ack && (
              <div style={box(null)}>
                已確認：{av.ack.category_label}<br />
                <span style={{ color: C.sub }}>{av.ack.acked_by}・{av.ack.acked_at}</span>
              </div>
            )}
            {av.stale && (
              <div style={box(C.warning)}>
                之前確認過差 {signed(av.stale.diff)}（{av.stale.reason}），之後數字變了，要重新確認。
              </div>
            )}
            <div style={{ ...labelStyle, marginBottom: 6 }}>原因</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
              {cats.map((c) => {
                const on = av.category === c.key;
                return (
                  <button type="button" key={c.key}
                    onClick={() => setAckView((v) => ({ ...v, category: c.key }))}
                    style={on
                      ? softBadge('#667eea', { fontSize: 12, padding: '5px 12px', cursor: 'pointer' })
                      : { fontSize: 12, padding: '5px 12px', borderRadius: 5, cursor: 'pointer',
                        background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}`, color: C.sub }}>
                    {c.label}
                  </button>
                );
              })}
            </div>
            <div style={{ ...labelStyle, marginBottom: 6 }}>說明</div>
            <textarea rows={3} value={av.reason}
              onChange={(e) => { const t = e.target.value; setAckView((v) => ({ ...v, reason: t })); }}
              placeholder="例：長安人員出錯的補償金，已歸到 777080 買幣"
              style={{ ...inputStyle, width: '100%', padding: '8px 10px', fontSize: 13, resize: 'vertical' }} />
            <div style={{ fontSize: 12, color: C.dim, marginTop: 8, lineHeight: 1.6 }}>
              確認後這格會變成灰色「已確認」，並通知 Discord。
              之後會計或系統的數字有變動，確認會自動失效，要再確認一次。
            </div>
            {av.error && <div style={{ color: C.danger, marginTop: 10 }}>{av.error}</div>}
          </ModalBody>
          {/* 主題的 modal-footer 是 space-between：左邊一顆、右邊一組（同俱樂部管理頁的「取消 … 保存」） */}
          <ModalFooter style={{ ...modalFooterStyle, padding: '12px 16px' }}>
            {av.ack ? (
              <Button size="sm" onClick={revokeAck} disabled={!!av.busy}
                style={softBadge(C.danger, { fontSize: 13, padding: '8px 16px', borderRadius: 6 })}>
                {av.busy === 'revoke' && <TinyBusy style={{ marginRight: 6 }} />}撤銷確認
              </Button>
            ) : (
              <Button size="sm" onClick={closeAck} disabled={!!av.busy} style={cancelBtnStyle}>取消</Button>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              {av.ack && (
                <Button size="sm" onClick={closeAck} disabled={!!av.busy} style={cancelBtnStyle}>取消</Button>
              )}
              <Button size="sm" onClick={submitAck} disabled={!canSubmit} style={saveBtnStyle(!canSubmit)}>
                {av.busy === 'save' && <TinyBusy light style={{ marginRight: 6 }} />}
                {av.ack ? '更新說明' : '確認差異'}
              </Button>
            </div>
          </ModalFooter>
        </>)}
      </Modal>
    );
  };

  // 告警（v2.46.0）：有預期外的事就掛在頁面最上面，同時送 Discord 給 Brad。
  // 規則（何時開、何時關、哪些送 Discord）都在後端 alerts.py，這裡只負責顯示。
  // info（例：只是記錯俱樂部、總數一致）收在最後一行，不跟要處理的混在一起。
  //
  // 平常收合成一行醒目的色條（Brad：主要是給會計看的，要夠醒目、但點開才展開）。
  // 後端已經把「實際交收沒填 / 對不上」排在最前面，色條上也另外標出幾件是交收的事。
  const openAlerts = health?.alerts || [];
  const renderAlerts = () => {
    const main = openAlerts.filter((a) => a.severity !== 'info');
    const info = openAlerts.filter((a) => a.severity === 'info');
    if (!main.length && !info.length) return null;
    const hasError = main.some((a) => a.severity === 'error');
    const tone = main.length ? (hasError ? C.danger : C.warning) : C.dim;
    const settleCount = main.filter((a) => (a.kind || '').startsWith('settlement_')).length;
    const item = (a) => (
      <div key={a.key} style={{ marginTop: 6 }}>
        <span style={{ color: a.severity === 'error' ? C.danger : a.severity === 'warn' ? C.warning : C.dim,
          marginRight: 6 }}>●</span>
        <span style={{ fontWeight: 600, color: a.severity === 'info' ? C.sub : C.text }}>{a.title}</span>
        <div style={{ fontSize: 12, color: C.sub, marginLeft: 16, lineHeight: 1.6 }}>
          {a.detail}
          <span style={{ color: C.dim, marginLeft: 6 }}>（{fmtSyncTime(a.first_seen, true)} 起）</span>
        </div>
      </div>
    );
    const tint = main.length ? (hasError ? 'rgba(245,54,92,' : 'rgba(251,99,64,') : 'rgba(255,255,255,';
    return (
      <div style={{ marginBottom: 12 }}>
        <button type="button" onClick={() => setAlertsOpen((o) => !o)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left',
            background: `${tint}${main.length ? '0.18)' : '0.04)'}`,
            border: `1px solid ${main.length ? tone : C.border}`,
            borderRadius: alertsOpen ? '8px 8px 0 0' : 8,
            padding: '9px 14px', cursor: 'pointer',
            color: main.length ? C.text : C.dim, fontSize: main.length ? 14 : 12,
            fontWeight: main.length ? 700 : 500,
          }}>
          <i className="fa fa-exclamation-triangle" style={{ color: tone }} />
          <span>{main.length ? `有 ${main.length} 件事要確認` : `${info.length} 則小提醒（不影響交收）`}</span>
          {settleCount > 0 && (
            <span style={{ fontSize: 12, fontWeight: 600, color: tone, borderRadius: 10,
              padding: '1px 9px', background: 'rgba(0,0,0,0.25)' }}>
              其中 {settleCount} 件是實際交收
            </span>
          )}
          <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 600, color: C.sub }}>
            {alertsOpen ? '收起 ▴' : '點開看 ▾'}
          </span>
        </button>
        {alertsOpen && (
          <div style={{
            border: `1px solid ${main.length ? tone : C.border}`, borderTop: 'none',
            borderRadius: '0 0 8px 8px', background: `${tint}0.06)`,
            padding: '4px 14px 12px', fontSize: 13,
          }}>
            {main.map(item)}
            {main.length > 0 && info.length > 0 && (
              <details style={{ marginTop: 10, color: C.dim, fontSize: 12 }}>
                <summary style={{ cursor: 'pointer' }}>另有 {info.length} 則小提醒（不影響交收）</summary>
                {info.map(item)}
              </details>
            )}
            {!main.length && info.map(item)}
          </div>
        )}
      </div>
    );
  };

  // 控制列順序：**先選看什麼（幣別）→ 再選怎麼看（粒度）→ 最後選看多久（期間）**
  // 每一組都有小標，不然三排 pill 擠在一起看不出各自在切什麼
  const lbl = { fontSize: 11, color: C.dim, letterSpacing: '.04em' };
  const dateInput = {
    background: C.inputBg, color: C.text, border: `1px solid ${C.border}`,
    borderRadius: 6, padding: '4px 8px', fontSize: 13,
  };
  const controlBar = (
    <div className="d-flex flex-wrap align-items-center mb-2" style={{ gap: 8 }}>
      <span style={lbl}>幣別</span>
      <Seg accent={ACCENT.currency} value={currency}
        onChange={(v) => { setCurrency(v); setVendor('all'); if (v !== 'usdt') setUnit('twd'); }}
        options={[{ key: 'all', label: '全部' }, { key: 'usdt', label: 'USDT' },
          { key: 'twd', label: '台幣' }]} />
      {currency === 'twd' && twdVendors.length > 1 && (
        <Seg accent={ACCENT.vendor} value={vendor} onChange={setVendor}
          options={[{ key: 'all', label: '全部' },
            ...twdVendors.map((x) => ({ key: x.source_id, label: x.display_name || x.source_id }))]} />
      )}

      {/* 單位切換只在幣別=USDT 時出現。
          🚫 幣別=全部 不給切 USDT —— 台幣幣商的 amount_usdt 恆為 0，
             加總出來的「USDT 總額」會靜默漏掉整個台幣側，而畫面還寫著「全部」。 */}
      {currency === 'usdt' && (<>
        <span style={lbl}>單位</span>
        <Seg accent={ACCENT.unit} value={unit} onChange={setUnit}
          options={[{ key: 'twd', label: '等值台幣' }, { key: 'usdt', label: 'USDT' }]} />
      </>)}

      <span style={{ width: 1, height: 20, background: C.border, margin: '0 6px' }} />

      <span style={lbl}>粒度</span>
      <Seg accent={ACCENT.granularity}
        options={[{ key: 'month', label: '月' }, { key: 'week', label: '週' }, { key: 'day', label: '日' }]}
        value={granularity} onChange={goToGranularity} />

      <span style={{ width: 1, height: 20, background: C.border, margin: '0 6px' }} />

      <span style={lbl}>期間</span>
      <input type="date" value={range?.from || ''}
        onChange={(e) => onPickDate('from', e.target.value)} style={dateInput} />
      <span style={{ color: C.dim }}>–</span>
      <input type="date" value={range?.to || ''}
        onChange={(e) => onPickDate('to', e.target.value)} style={dateInput} />
      {/* 一鍵回到全部期間。
        🔑 這是**明確的逃生口** —— 鑽下去之後期間會被縮窄，雖然切粒度會盡量
           還原，但那依賴「去過那一層」的記憶；使用者不該需要理解那套規則才回得去。
           一顆看得到的按鈕比任何隱性規則都可靠。
        ⚠️ 已經在全部期間時 disable，不然它看起來像「按了會有事發生」卻沒反應。 */}
      <button
      type="button"
      disabled={isFullRange}
      onClick={() => { setRangeByGran({}); setRange(fullRange); }}
      title={isFullRange ? '目前已經是全部期間' : `回到 ${fullRange.from} ~ ${fullRange.to}`}
      onMouseEnter={(e) => { if (!isFullRange) e.currentTarget.style.background = 'rgba(255,255,255,0.13)'; }}
      onMouseLeave={(e) => { if (!isFullRange) e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
      style={{
        height: 30, boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '0 12px', borderRadius: 7, marginLeft: 2,
        background: 'rgba(255,255,255,0.07)', border: `1px solid ${C.border}`,
        color: isFullRange ? C.dim : C.text,
        fontSize: 12, fontWeight: 600, lineHeight: 1,
        cursor: isFullRange ? 'default' : 'pointer', opacity: isFullRange ? 0.55 : 1,
        transition: 'background 0.16s ease',
      }}
      >
      <i className="fa fa-clock-rotate-left" style={{ fontSize: 11 }} />全部期間
      </button>

      <div className="d-flex align-items-center flex-wrap"
        style={{ marginLeft: 'auto', gap: 6, justifyContent: 'flex-end' }}>
        {syncSources.length
          ? syncSources.map(renderSyncChip)
          : <span style={{ fontSize: 12, color: C.dim }}>尚未同步</span>}
        {/* 做成真的看起來可按的按鈕，但用主題的 accent 而不是 bootstrap 的亮紫漸層 */}
      <button type="button" onClick={doSync} disabled={syncing}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: syncing ? 'rgba(102,126,234,0.25)'
              : 'linear-gradient(135deg, rgba(102,126,234,0.9), rgba(118,75,162,0.9))',
            color: '#fff', border: '1px solid rgba(102,126,234,0.6)', borderRadius: 6,
            padding: '5px 14px', fontSize: 13, fontWeight: 600, marginLeft: 4,
            cursor: syncing ? 'default' : 'pointer',
            boxShadow: syncing ? 'none' : '0 2px 8px rgba(102,126,234,0.35)',
          }}>
          {syncing ? <TinyBusy light /> : <i className="fa fa-refresh" />}
          {syncing ? '同步中' : '立即同步'}
        </button>
      </div>
    </div>
  );

  // 歸集（子錢包 → 主錢包）的鏈上手續費：**只有自營 USDT 有這回事**。
  //
  // 「全部」也顯示是刻意的 —— 在總覽藏起來的話，各幣別相加不等於全部，
  // 看的人會以為漏了東西。「台幣」不顯示，那邊沒有歸集。
  const showSweep = currency === 'all' || currency === 'usdt';

  // 金額欄位的單位。**表頭與提示共用這一個**，不要各寫一份
  //（getWeekExpenses 與 CashflowEditCell 都吃過兩份手抄漂掉的虧）。
  //
  // ⚠️ 「等值台幣」這四個字是必要的：USDT 側的台幣是幣商**逐筆用成交當時匯率**
  //    換算出來的，不是真的有台幣在動。實測出金 twd/usdt 逐筆一致（32.2597~32.2599）、
  //    入金 (twd−bonus)/usdt 也一致（32.2600）—— 換算是對的，但它就是換算。
  const amountUnit = unit === 'usdt' ? 'USDT'
    : currency === 'twd' ? '台幣'
      : currency === 'usdt' ? '等值台幣' : '台幣';
  // 幣別「全部」是混的：台幣幣商是真的台幣，USDT 側是換算 —— 要講出來
  const fxNote = unit === 'twd' && currency !== 'twd'
    ? (currency === 'usdt'
        ? 'USDT 以成交當時匯率逐筆換算成等值台幣；入金為「實際收到」，不含我方贈送的加碼'
        : '台幣幣商為實際台幣；USDT 以成交當時匯率換算成等值台幣（不含加碼）')
    : null;

  // ---------- 表格 ----------
  const renderTable = () => {
    const rows = [];
    let lastYear = null;
    // 新的在上面，所以進行中的那一期自然是第一列（見 isClosed 上方的說明）
    const ordered = [...buckets].reverse();

    ordered.forEach((b) => {
      const y = b.key.slice(0, 4);
      const live = !isClosed(b.key);

      if (y !== lastYear) {
        rows.push(
          <tr key={`y-${y}`}>
            <td colSpan={6 + (showSweep ? 1 : 0) + (vendorCols.length > 0 ? 3 : 0) + 1}
              style={{ textAlign: 'center', padding: '6px',
              background: 'rgba(255,255,255,0.05)', fontWeight: 600, letterSpacing: '.05em' }}>
              {y} 年
            </td>
          </tr>);
        lastYear = y;
      }

      // ⚠️ 佣金**每一列都能填**。以前只在「涵蓋該月最後一天」那一列開放編輯，
      // 因為歸屬月份是從「你點了哪一列」推出來的（位置即語意）—— 但會計沒有
      // 那種規律（實測 8 次裡 2 次不在月底那週），UI 反而逼他填在不自然的地方。
      // 現在位置與月份拆開：填在哪一週隨他，月份在編輯時明選。
      // `closing` 只剩一個用途：這一列若是某月的月底列，把該月的「系統應付」
      // 當提示顯示出來，讓會計填之前就看得到金額。
      const closing = closingMonthOf(b.key);
      const commissionOf = (sourceId) => {
        const key = `vendor_commission__${sourceId}`;
        const k = `${b.key}|${key}`;
        const month = manualMonth[k] || null;
        // 有登記 → 用它的歸屬月份；沒登記且這列是月底列 → 拿該月當提示
        const refMonth = month || (manual[k] == null ? closing : null);
        const vc = refMonth
          ? (costs[refMonth]?.vendors || []).find((x) => x.source_id === sourceId)
          : null;
        return { key, k, month, refMonth, vc, isHint: !month && manual[k] == null };
      };

      rows.push(
        // 進行中：虛線框 + 淡底，跟已結束的期間一眼分得開（outline 不佔版面、不推擠欄寬）
        <tr key={b.key} style={live ? {
          outline: '1px dashed rgba(17,205,239,0.55)', outlineOffset: -2,
          background: 'rgba(17,205,239,0.05)' } : undefined}>
          <td style={{ ...TD, fontWeight: 600 }}>
            {label(b.key)}
            {live && (
              <div style={{ marginTop: 4, fontSize: 11, fontWeight: 500, lineHeight: 1.6, color: C.dim }}>
                <span style={{ display: 'inline-block', padding: '0 7px', borderRadius: 9,
                  background: 'rgba(17,205,239,0.15)', color: '#11cdef', marginRight: 6 }}>
                  <i className="fa fa-clock" style={{ marginRight: 4 }} />進行中
                </span>
                {progressOf(b.key) && `第 ${progressOf(b.key)} 天`}
                {dataAsOf && <div>截至 {fmtSyncTime(dataAsOf, true)}</div>}
              </div>
            )}
          </td>
          {/* ⚠️ 這一欄是「玩家實際給了多少」，不含我方贈送的加碼（常態 2%、開幕期 10%）。
              🚫 **畫面上不顯示加碼金額** —— 這張表記的是「真的收到的錢」，
                 加碼是我方發出去的遊戲幣、是成本不是收入，擺在入金旁邊只會混淆
                （Brad 2026-09-11）。
              🔑 資料層不動：`buy_bonus_twd` 仍在 payload 裡，扣除的邏輯也在後端，
                 所以入金金額本來就是乾淨的。加碼由會計在營收數據的「額外費用」
                 人工登記（刻意不自動化，多一層人為檢查）。 */}
          <td style={{ ...TD, color: C.inflow }}>
            {fmt(unit === 'usdt' ? b.buy_usdt : b.buy_twd)}</td>
          <td style={{ ...TD, color: C.outflow }}>
            {fmt(unit === 'usdt' ? b.sell_usdt : b.sell_twd)}</td>
          {(() => { const net = unit === 'usdt' ? b.buy_usdt - b.sell_usdt : b.net_twd;
            return (
              <td style={{ ...TD, color: net >= 0 ? C.success : C.danger, fontWeight: 600 }}>
                {fmt(net)}</td>); })()}
          <td style={TD}>{b.buy_count_available ? fmt(b.buy_count) : '—'}</td>
          <td style={TD}>{b.sell_count_available ? fmt(b.sell_count) : '—'}</td>
          {/* 附註一律走 icon + hover tooltip，不佔欄寬。
              兩種 icon 刻意不同色：認列週是一般註記（dim），費用不明是**資料品質警告**
              （warning）—— 警訊跟註記長一樣的話，警訊就等於不存在。 */}
          {showSweep && (() => {
            const settledElsewhere = b.sweep_settled_weeks?.length === 1
              && b.sweep_settled_weeks[0] !== b.key;
            return (
              <td style={TD}>
                {b.sweep_count
                  ? fmt(unit === 'usdt' ? b.sweep_fee_usdt : b.sweep_fee_twd)
                  : <span style={{ color: C.dim }}>—</span>}
                {/* ⚠️ 幣商連費用欄位都沒有的那幾筆是「未知」不是「沒花錢」。
                    不以 0 計入，但一定要看得見 —— 不講的話上面那個數字會被讀成全部。 */}
                {b.sweep_fee_unknown > 0 && (
                  <TinyTooltip
                    maxWidth={340}
                    content={
                      <>
                        這一期另有 <strong>{b.sweep_fee_unknown} 次</strong>歸集，
                        幣商那邊<strong>連費用欄位都沒有</strong>。
                        <br />
                        那是「不知道花了多少」，<strong>不是「沒花錢」</strong> ——
                        所以沒有以 0 計入左邊的金額。
                      </>
                    }
                  >
                    <i className="fa fa-exclamation-triangle"
                      style={{ color: C.warning, marginLeft: 5, fontSize: 11 }} />
                  </TinyTooltip>
                )}
                {/* 認列到別的期間時才標。落在本期就不用 —— 常態情況畫面要乾淨，
                    常駐的標記等於沒有標記。 */}
                {settledElsewhere && (
                  <TinyTooltip
                    maxWidth={360}
                    content={
                      <>
                        這筆錢<strong>發生在這一期</strong>，但在「總淨營收」是記在{' '}
                        <strong>{b.sweep_settled_weeks[0]}</strong> 那一週。
                        <br />
                        兩邊對不起來是正常的：這一欄按<strong>實際發生日期</strong>分期
                        （才對得上鏈上紀錄），帳則按<strong>認列週</strong>。
                      </>
                    }
                  >
                    <i className="fa fa-info-circle"
                      style={{ color: C.dim, marginLeft: 5, fontSize: 11 }} />
                  </TinyTooltip>
                )}
              </td>
            );
          })()}
          {/* 進行中的那一期還不能結算：佣金 / 實際交收三欄合成一格、不給填 */}
          {vendorCols.length > 0 && live && (
            <td colSpan={3} style={{ ...TD, fontSize: 12, color: C.dim }}>結束後才能填</td>
          )}
          {vendorCols.length > 0 && !live && (<>
            {/* 三欄固定，每格裡面一個幣商一列、中間有分隔線。
                只有一個幣商時不顯示標籤（沒必要）。 */}
            <td style={TD}>
              <VendorStack vendors={vendorCols} render={(v) => {
                const { vc, refMonth, isHint } = commissionOf(v.source_id);
                if (!vc || vc.expected == null) return <span style={{ color: C.dim }}>—</span>;
                // 還沒有人登記時只是「提示」，用低透明度跟已對帳的區分開
                return (
                  <TinyTooltip maxWidth={380}
                    content={<CommissionFormula vc={vc} month={refMonth} name={v.display_name} />}>
                    <span style={{ opacity: isHint ? 0.5 : 1 }}>
                      {fmt(vc.expected)}
                      <span style={{ fontSize: 10, color: C.dim, marginLeft: 4 }}>{refMonth}</span>
                      <i className="fa fa-info-circle"
                        style={{ color: C.dim, marginLeft: 5, fontSize: 11 }} />
                    </span>
                  </TinyTooltip>
                );
              }} />
            </td>
            <td style={TD}>
              <VendorStack vendors={vendorCols} render={(v) => {
                const { key, k, month } = commissionOf(v.source_id);
                return (
                  <CashflowEditCell canWrite={canWrite} withMonth
                    value={manual[k]} month={month}
                    defaultMonth={defaultCommissionMonth(v.source_id)}
                    monthNotice={commissionNotice(v.source_id, month)}
                    // ⚠️ week 存**這一列的週期**，不是月字串。原本傳的是 closing
                    //（'2026-09' 那種），而 payFee 的查詢用定寬週期比對，
                    // 月字串會被排除 → 從畫面填的佣金永遠不會進總淨營收。
                    onSave={(val, _n, mo) => saveManual(
                      b.key, key, '台幣幣商佣金', val, mo)} />
                );
              }} />
            </td>
            <td style={TD}>
              <VendorStack vendors={vendorCols} render={(v) => {
                // 系統算的交收（入金 − 出金，這一週、這一家）直接放在格子下面（v2.46.0）。
                // 會計照樣自己填實際交收，填的時候兩個數字就在眼前，對不上當場看得到。
                // v2.52.0 起系統數字與相符與否由後端算（跟告警同一支），這裡只負責畫。
                const weekly = /^\d{8}-\d{8}$/.test(b.key);
                const chk = weekly ? checks[`${b.key}|${v.source_id}`] : null;
                const actual = manual[`${b.key}|vendor_settlement__${v.source_id}`];
                return (
                  <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <CashflowEditCell canWrite={canWrite}
                      // 週已結束、這家那時已經開始合作、還沒填 → 醒目的「待填」
                      overdue={weekly && isClosed(b.key)
                        && !!v.earliest_data_date && v.earliest_data_date <= iso(b.key, 9)}
                      value={actual}
                      onSave={(val) => saveManual(b.key,
                        `vendor_settlement__${v.source_id}`, '幣商實際交收', val)} />
                    {chk && (
                      <span style={{ fontSize: 11, color: C.dim, marginTop: 2, whiteSpace: 'nowrap' }}>
                        系統 {fmt(chk.calculated)}
                        {chk.status === 'match' && (
                          <span style={{ color: C.success, marginLeft: 6 }}>✓ 相符</span>)}
                        {(chk.status === 'mismatch' || chk.status === 'acknowledged') && (
                          <DiffMark chk={chk} canWrite={canWrite} style={{ marginLeft: 6 }}
                            text={`差 ${chk.diff > 0 ? '+' : ''}${fmt(chk.diff)}`}
                            onOpen={() => openAck({
                              kind: 'settlement', vendor: v, period: b.key,
                              periodLabel: `${label(b.key)} 實際交收`, chk })} />
                        )}
                      </span>
                    )}
                  </span>
                );
              }} />
            </td>
          </>)}
          {/* 備註也跟著上下疊，跟左邊三欄的幣商行對齊。
              幣商名稱不用重複（左邊圓點已經標了），但**主詞「佣金」要留著** ——
              只寫「相符」看不出是什麼相符。 */}
          <td style={{ ...TD, fontSize: 12, width: 170 }}>
            {(!b.buy_count_available || b.mismatch > 0) && (
              <div style={{ marginBottom: 4 }}>
                {!b.buy_count_available && <span style={{ color: C.dim }}>無逐筆</span>}
                {/* 點開看是哪一天、哪一家、差在哪（v2.46.0）—— 只寫個數，看的人不知道發生什麼事 */}
                {/* 全部只是「記在不同俱樂部、總數一致」（mismatch_warn=0）就畫成灰色 ——
                    不影響交收，不是要人處理的警告（v2.52.0） */}
                {b.mismatch > 0 && (
                  <button type="button" onClick={() => openMismatch(b.key)}
                    style={{
                      marginLeft: 6, padding: '1px 8px', borderRadius: 10, cursor: 'pointer',
                      fontSize: 12, fontWeight: 600, lineHeight: 1.5,
                      ...(b.mismatch_warn > 0
                        ? { border: `1px solid ${C.warning}`, background: 'rgba(251,99,64,0.12)', color: C.warning }
                        : { border: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.05)', color: C.dim }),
                    }}>
                    {b.mismatch_warn > 0 ? `彙總不符 ${b.mismatch}` : '記在不同俱樂部'}
                    {' '}<i className="fa fa-magnifying-glass" style={{ fontSize: 10 }} />
                  </button>
                )}
              </div>
            )}
            {vendorCols.length > 0 && (
              <VendorStack vendors={vendorCols} showLabel={false} align="center"
                render={(v) => {
                  // 對帳結果跟著「實際登記的那一列」走，不再綁在月底那列
                  const { vc, isHint, refMonth } = commissionOf(v.source_id);
                  if (!vc || isHint || vc.status === 'pending') return '';
                  if (vc.status === 'match') return <span style={{ color: C.success }}>佣金相符</span>;
                  if (vc.status === 'mismatch' || vc.status === 'acknowledged') {
                    const chk = { ...vc, calculated: vc.expected };
                    return (
                      <DiffMark chk={chk} canWrite={canWrite} text={`佣金差 ${fmt(vc.diff)}`}
                        onOpen={() => openAck({
                          kind: 'commission', vendor: v, period: refMonth,
                          periodLabel: `${refMonth} 佣金`, chk })} />
                    );
                  }
                  return <span style={{ color: C.danger }}>佣金差 {fmt(vc.diff)}</span>;
                }} />
            )}
          </td>
        </tr>);
    });

    return (
      <div className="table-responsive">
        <Table className="tablesorter">
          <thead className="text-primary">
            <tr>
              <th style={{ ...TH('transparent'), width: 150 }}>期間</th>
              <th style={TH('rgba(74,144,226,0.2)')}>入金<br /><small>{amountUnit}</small></th>
              <th style={TH('rgba(232,118,58,0.2)')}>出金<br /><small>{amountUnit}</small></th>
              <th style={TH('rgba(45,206,137,0.2)')}>淨額<br /><small>{amountUnit}</small></th>
              <th style={TH('rgba(74,144,226,0.12)')}>入金筆數</th>
              <th style={TH('rgba(232,118,58,0.12)')}>出金筆數</th>
              {showSweep && (
                <th style={TH('rgba(245,54,92,0.12)')}>
                  {/* 副標不重複單位 —— 左邊三欄已經講過了，四個欄位都寫一次是噪音。
                      這個位置拿來講「這是什麼」比「什麼單位」有價值。
                      寫鏈名 TRON 而不是 TronLink / GasFree：前者不會變，
                      後者是錢包品牌與服務名，換供應商就過時。 */}
                  <TinyTooltip
                    maxWidth={380}
                    content={
                      <>
                        玩家的 USDT 是打進各自的<strong>子錢包</strong>，出金卻一律從
                        <strong>主錢包</strong>出，所以要定期把子錢包的錢「歸集」回主錢包。
                        <br />
                        每個成功歸集的錢包會被<strong>預扣一筆鏈上手續費</strong> ——
                        那筆錢離開子錢包又沒到主錢包，是付給錢包服務商的，屬於我方成本。
                        <br />
                        這一欄按<strong>歸集實際發生的日期</strong>分期（所以對得上鏈上紀錄）；
                        帳認列在哪一週是另一回事，見「總淨營收」的金流費用。
                      </>
                    }
                  >
                    <span>歸集手續費 <i className="fa fa-circle-question" style={{ fontSize: 11, opacity: 0.7 }} /></span>
                  </TinyTooltip>
                  <br /><small>TRON</small></th>
              )}
              {/* ⚠️ 欄位**固定三欄**，多個幣商是在同一格裡上下疊。
                  分成「一家三欄」的話，來了第三家版面就爆了。 */}
              {vendorCols.length > 0 && (<>
                <th style={TH('rgba(245,54,92,0.15)')}>幣商佣金<br /><small>系統計算</small></th>
                <th style={TH('rgba(245,54,92,0.25)')}>幣商佣金<br /><small>會計填寫</small></th>
                <th style={TH('rgba(17,205,239,0.2)')}>幣商實際交收<br /><small>會計填寫</small></th>
              </>)}
              <th style={TH('transparent')}>備註</th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </Table>
        {!canWrite && (
          <div style={{ fontSize: 12, color: C.dim, padding: '8px 4px' }}>
            <i className="fa fa-lock mr-1" />只有指定帳號可以編輯「會計填寫」欄位，其餘唯讀。
          </div>
        )}
      </div>
    );
  };

  // ---------- 總覽 ----------
  const val = (b, dir) => {
    if (metric === 'count') return b[`${dir}_count_available`] ? b[`${dir}_count`] : null;
    return unit === 'usdt' ? b[`${dir}_usdt`] : b[`${dir}_twd`];
  };

  // 買賣**重疊**一粗一細（grouped:false），不並排 ——
  // 並排會互相擠壓，而出金總是矮很多；重疊才看得出比例，也不會互相蓋掉
  const chartData = {
    labels: buckets.map((b) => label(b.key)),
    datasets: [
      ...(show !== 'sell' ? [{
        label: '買幣（入金）', backgroundColor: C.inflow, data: buckets.map((b) => val(b, 'buy')),
        grouped: false, barPercentage: 0.94, categoryPercentage: 0.84, order: 2, borderRadius: 2,
      }] : []),
      ...(show !== 'buy' ? [{
        label: '賣幣（出金）', backgroundColor: C.outflow, data: buckets.map((b) => val(b, 'sell')),
        grouped: false, barPercentage: 0.44, categoryPercentage: 0.84, order: 1, borderRadius: 2,
      }] : []),
    ],
  };
  const chartOpts = {
    maintainAspectRatio: false,
    // 點一根長條就鑽進它的下一層（月→週→日）。
    // ⚠️ Chart.js 3 的簽章是 (event, elements, chart)，elements[0].index 是那一根。
    onClick: (evt, elements) => {
      if (!canDrill || !elements?.length) return;
      doDrill(elements[0].index);
    },
    // 可鑽的時候游標才變手指 —— 粒度=日沒有下一層，給了 pointer 是騙人的
    onHover: (evt, elements) => {
      const c = evt?.native?.target;
      if (c) c.style.cursor = (canDrill && elements?.length) ? 'pointer' : 'default';
    },
    plugins: {
      legend: { labels: { color: C.sub, boxWidth: 12, font: { size: 11 } } },
      tooltip: { callbacks: { label: (c) =>
        `${c.dataset.label}: ${c.parsed.y === null ? '無資料' : fmt(c.parsed.y)}` } },
    },
    scales: {
      x: { ticks: { color: C.dim, font: { size: 10 }, maxRotation: 45 },
        grid: { color: 'rgba(255,255,255,0.04)' } },
      y: { beginAtZero: true,
        ticks: { color: C.dim, font: { size: 10 },
          callback: (v) => (Math.abs(v) >= 1e6 ? `${(v / 1e6).toFixed(1)}M`
            : Math.abs(v) >= 1e3 ? `${(v / 1e3).toFixed(0)}K` : v) },
        grid: { color: 'rgba(255,255,255,0.06)' } },
    },
  };

  const Kpi = ({ k, v, sub, color }) => (
    <div style={{ flex: '1 1 150px', minWidth: 150, background: C.inputBg,
      border: `1px solid ${C.border}`, borderRadius: 8, padding: '12px 15px' }}>
      <div style={{ fontSize: 11, color: C.dim }}>{k}</div>
      <div style={{ fontSize: 21, fontWeight: 600, marginTop: 4, color: color || C.text }}>{v}</div>
      {sub && <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>{sub}</div>}
    </div>
  );

  const renderSummary = () => (
    <>
      <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: 10 }}>
        <span style={{ fontSize: 12, color: C.dim }}>俱樂部</span>
        <select value={club} onChange={(e) => setClub(e.target.value)}
          style={{ background: C.inputBg, color: C.text, border: `1px solid ${C.border}`,
            borderRadius: 6, padding: '4px 10px', fontSize: 13, minWidth: 150 }}>
          {/* 只有走儲值制的俱樂部才會有金流資料（實測就這四家），
              寫「全部俱樂部」會讓人以為聯盟 36 家都在裡面 */}
          <option value="all">全部儲值制俱樂部</option>
          {clubOptions.map((c) => (
            <option key={c.club_id} value={c.club_id}>
              {c.club_name ? `${c.club_name}（${c.club_id}）` : c.club_id}
            </option>
          ))}
        </select>
        <span style={{ width: 1, height: 20, background: C.border, margin: '0 4px' }} />
        <span style={{ fontSize: 12, color: C.dim }}>顯示</span>
        <Seg accent={ACCENT.show} value={show} onChange={setShow}
          options={[{ key: 'buy', label: '買幣' }, { key: 'sell', label: '賣幣' },
            { key: 'both', label: '一起顯示' }]} />
        <Seg accent={ACCENT.metric} value={metric} onChange={setMetric}
          options={[{ key: 'count', label: '筆數' }, { key: 'amount', label: '金額' }]} />
        {/* 🚫 幣別=全部 時不給切 USDT —— 台幣幣商的 amount_usdt 恆為 0，
               加總出來的「USDT 總額」會靜默漏掉整個台幣側，而 KPI 還寫著「全部」。 */}
        {metric === 'amount' && currency === 'usdt' && (
          <Seg accent={ACCENT.unit} value={unit} onChange={setUnit}
            options={[{ key: 'twd', label: '等值台幣' }, { key: 'usdt', label: 'USDT' }]} />
        )}
        {metric === 'amount' && fxNote && (
          <span style={{ fontSize: 11, color: C.dim }}>ⓘ {fxNote}</span>
        )}
      </div>

      <div className="d-flex flex-wrap mb-3" style={{ gap: 10 }}>
        <Kpi k="入金" v={fmt(unit === 'usdt' ? t.buy_usdt : t.buy_twd)} color={C.inflow} />
        <Kpi k="出金" v={fmt(unit === 'usdt' ? t.sell_usdt : t.sell_twd)} color={C.outflow} />
        <Kpi k="淨額" v={fmt(unit === 'usdt' ? (t.buy_usdt - t.sell_usdt) : t.net_twd)} />
        <Kpi k="買賣比" v={t.buy_sell_ratio ? `${t.buy_sell_ratio} : 1` : '—'} />
        <Kpi k="入金筆數" v={t.buy_count_available ? fmt(t.buy_count) : '—'}
          sub={t.buy_count_available ? null : '部分期間無逐筆'} />
        <Kpi k="實際台幣金流" v={fmt(t.buy_actual_twd)} sub="佣金按這個算" />
        {t.mismatch > 0 && <Kpi k="彙總 vs 明細不符" v={t.mismatch} color={C.warning}
          sub="廠商月報表與自己的逐筆" />}
      </div>

      {/* 縮放層級的麵包屑。
          🔑 每一層都可以直接點，而且跟上面那組粒度按鈕**走同一支 goToGranularity()** ——
             使用者不必記得「哪個入口會保留期間、哪個不會」。
          ⚠️ 只列出「有記到期間」的較粗層級：沒去過的層級點了也只是沿用目前期間，
             把它畫成路徑會謊稱你來過那裡。 */}
      {coarserGran && GRAN_ORDER.slice(0, GRAN_ORDER.indexOf(granularity))
        .some((g) => rangeByGran[g]) && (
        <div style={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8,
          marginBottom: 10, fontSize: 12, color: C.dim,
        }}>
          <button
            type="button"
            onClick={() => goToGranularity(coarserGran)}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.13)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
            style={{
              height: 28, boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center',
              gap: 6, padding: '0 12px', borderRadius: 7,
              background: 'rgba(255,255,255,0.07)', border: `1px solid ${C.border}`,
              color: C.text, fontSize: 12, fontWeight: 600, lineHeight: 1, cursor: 'pointer',
              transition: 'background 0.16s ease',
            }}
          >
            <i className="fa fa-arrow-left" style={{ fontSize: 10 }} />
            返回{GRAN_LABEL[coarserGran]}
          </button>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {GRAN_ORDER.map((g, idx) => {
              const here = g === granularity;
              const r = here ? range : rangeByGran[g];
              if (!r) return null;
              const isAncestor = idx < GRAN_ORDER.indexOf(granularity);
              return (
                <span key={g} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {idx > 0 && <i className="fa fa-chevron-right" style={{ fontSize: 9, opacity: 0.5 }} />}
                  <button
                    type="button"
                    disabled={here}
                    onClick={() => goToGranularity(g)}
                    style={{
                      background: 'transparent', border: 'none', padding: 0,
                      color: here ? C.text : (isAncestor ? C.sub : C.dim),
                      fontWeight: here ? 600 : 500, fontSize: 12,
                      cursor: here ? 'default' : 'pointer',
                      textDecoration: here ? 'none' : 'underline dotted',
                    }}
                  >
                    {GRAN_LABEL[g]}
                    <span style={{ opacity: 0.7, marginLeft: 5 }}>{r.from} ~ {r.to}</span>
                  </button>
                </span>
              );
            })}
          </span>
        </div>
      )}

      <div style={{ height: 340, marginBottom: 18 }}>
        {/* 重抓時由外層 <TinyLoadingOverlay active={loading}> 蓋住，圖表留著不閃掉 */}
        <Bar data={chartData} options={chartOpts} />
      </div>
      {canDrill && buckets.length > 0 && (
        <div style={{ fontSize: 11, color: C.dim, marginTop: -12, marginBottom: 14 }}>
          <i className="fa fa-hand-pointer mr-1" />
          點任一根長條可鑽進{GRAN_LABEL[drillInto(buckets[0].key, granularity)?.granularity] || ''}明細
        </div>
      )}

    </>
  );

  return (
    <div style={{ color: C.text }}>
      {/* 主 switch 只有「現在在用的」兩個檢視。
          歷史數據是偶爾才回頭看的東西，塞進主 switch 會讓它看起來跟另外兩個同等重要 ——
          改成右側低調的切換鈕，沿用頁面上「更多歷史聯盟」那顆既有的樣式。 */}
      <div className="d-flex flex-wrap align-items-center" style={{ gap: 10 }}>
        {view !== 'legacy' && (
          <Seg accent={ACCENT.view} value={view} onChange={(v) => onViewChange && onViewChange(v)}
            options={[
              { key: 'table', label: '表格', icon: 'fa-th-large' },
              { key: 'summary', label: '總覽', icon: 'fa-bar-chart' },
            ]} />
        )}
        <Nav pills className="rev-legacy-nav" style={{ '--tab-accent': '145,124,255', marginLeft: 'auto' }}>
          <NavItem>
            <NavLink href="#" className={classNames('rev-legacy-link', { active: view === 'legacy' })}
              onClick={(e) => {
                e.preventDefault();
                onViewChange && onViewChange(view === 'legacy' ? 'table' : 'legacy');
              }}>
              <i className={`fa ${view === 'legacy' ? 'fa-angle-left' : 'fa-archive'}`} />
              {view === 'legacy' ? '回到目前資料' : '歷史數據'}
            </NavLink>
          </NavItem>
        </Nav>
      </div>
      <div className="mt-3">
        {view === 'legacy' ? (renderLegacy ? renderLegacy() : null) : (
          <>
            {renderAlerts()}
            {controlBar}
            {view === 'table' && (
              <div style={{ fontSize: 12, color: C.dim, marginBottom: 8,
                display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center' }}>
                <span><i className="fa fa-info-circle mr-1" />
                  數字以各來源最後一次同步為準（右上角的狀態鈕）。</span>
                {fxNote && (
                  <span style={{ color: C.warning || C.dim }}>
                    <i className="fa fa-exchange mr-1" />{fxNote}</span>
                )}
              </div>
            )}
            {!series ? (
              loadError
                ? <div className="text-center py-5" style={{ color: C.danger || '#f5365c' }}>
                    <i className="fa fa-exclamation-triangle mr-2" />載入失敗：{loadError}</div>
                : <TinyLoading text="載入金流數據" variant="block" />
            ) : (
              // 已經有資料的重抓（切幣別 / 粒度 / 期間）蓋一層遮罩，表格不閃掉
              <TinyLoadingOverlay active={loading} text="更新金流數據">
                {view === 'summary' ? renderSummary() : renderTable()}
              </TinyLoadingOverlay>
            )}
          </>
        )}
      </div>
      {renderMismatchModal()}
      {renderAckModal()}
    </div>
  );
}
